import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { User, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

interface MentionListProps {
  items: Array<{
    id: number;
    name: string;
    email: string;
    avatar?: string;
  }>;
  command: (item: any) => void;
}

interface MentionListRef {
  onKeyDown: (event: { event: KeyboardEvent }) => boolean;
}

export const MentionList = forwardRef<MentionListRef, MentionListProps>(
  ({ items, command }, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);

    const selectItem = (index: number) => {
      const item = items[index];
      if (item) {
        command({
          id: item.id.toString(),
          label: item.name,
        });
      }
    };

    const upHandler = () => {
      setSelectedIndex((selectedIndex + items.length - 1) % items.length);
    };

    const downHandler = () => {
      setSelectedIndex((selectedIndex + 1) % items.length);
    };

    const enterHandler = () => {
      selectItem(selectedIndex);
    };

    useEffect(() => setSelectedIndex(0), [items]);

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }: { event: KeyboardEvent }) => {
        if (event.key === 'ArrowUp') {
          upHandler();
          return true;
        }

        if (event.key === 'ArrowDown') {
          downHandler();
          return true;
        }

        if (event.key === 'Enter') {
          enterHandler();
          return true;
        }

        return false;
      },
    }));

    if (items.length === 0) {
      return (
        <div className="bg-background border border-border rounded-lg shadow-lg p-3 text-sm text-muted-foreground">
          No users found
        </div>
      );
    }

    return (
      <div className="bg-background border border-border rounded-lg shadow-lg overflow-hidden">
        <div className="max-h-60 overflow-y-auto">
          {items.map((item, index) => (
            <button
              key={item.id}
              type="button"
              className={cn(
                'flex items-center w-full px-3 py-2 text-left text-sm hover:bg-muted transition-colors',
                index === selectedIndex && 'bg-muted'
              )}
              onClick={() => selectItem(index)}
            >
              <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-full mr-3 shrink-0">
                {item.avatar ? (
                  <img
                    src={item.avatar}
                    alt={item.name}
                    className="w-8 h-8 rounded-full"
                  />
                ) : (
                  <User className="w-4 h-4 text-primary" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{item.name}</div>
                <div className="text-xs text-muted-foreground truncate">{item.email}</div>
              </div>
              {index === selectedIndex && (
                <Check className="w-4 h-4 text-primary shrink-0 ml-2" />
              )}
            </button>
          ))}
        </div>
      </div>
    );
  }
);